/**
 * Brouillon d'exercice — conversions entre le formulaire et l'API.
 *
 * Même découpage que `foodDraft.ts` et `recipeDraft.ts` : l'éditeur ne
 * manipule que des chaînes (c'est ce qu'un `<input>` rend), et tout ce qui
 * ressemble à une conversion vit ici, testable sans monter un composant.
 *
 * ⚠️ **Aucun champ `source`, `mediaKey` ni `imageCount`.** Le premier est une
 * provenance que le serveur déduit ; les deux autres décrivent un dossier
 * déposé sur son disque. Les routes d'écriture les refusent (corps `strict`),
 * et le brouillon n'a donc rien à en dire.
 */

import type {
  ExerciseDTO,
  ExerciseInput,
  ExerciseLevel,
  UpdateExerciseBody,
} from '../../../../shared/types';

export interface ExerciseDraft {
  name: string;
  category: string;
  equipment: string;
  level: ExerciseLevel;
  force: '' | 'push' | 'pull' | 'static';
  mechanic: '' | 'compound' | 'isolation';
  /** Saisis en clair, séparés par des virgules — cf. `parseList`. */
  primaryMuscles: string;
  secondaryMuscles: string;
  instructions: string;
}

/**
 * ⚠️ **`category` par défaut à `strength` et `level` à `intermediate`**, plutôt
 * qu'à vide. Un exercice qu'on ajoute soi-même en salle est presque toujours du
 * renforcement, et faire choisir un niveau à quelqu'un qui veut juste noter
 * « presse à cuisses de ma salle » est une friction pour rien. Les deux restent
 * modifiables ; ce sont des défauts, pas des contraintes.
 */
export function emptyExerciseDraft(): ExerciseDraft {
  return {
    name: '',
    category: 'strength',
    equipment: '',
    level: 'intermediate',
    force: '',
    mechanic: '',
    primaryMuscles: '',
    secondaryMuscles: '',
    instructions: '',
  };
}

export function draftOfExercise(exercise: ExerciseDTO): ExerciseDraft {
  return {
    name: exercise.name,
    category: exercise.category,
    equipment: exercise.equipment ?? '',
    level: exercise.level,
    force: exercise.force ?? '',
    mechanic: exercise.mechanic ?? '',
    primaryMuscles: exercise.primaryMuscles.join(', '),
    secondaryMuscles: exercise.secondaryMuscles.join(', '),
    instructions: exercise.instructions ?? '',
  };
}

/**
 * « biceps, avant-bras » → `['biceps', 'avant-bras']`.
 *
 * ⚠️ **Une virgule, pas un sélecteur à choix multiples.** Le vocabulaire vient
 * du jeu importé (17 muscles) mais reste **ouvert** : l'utilisateur crée ses
 * exercices, et rien ne dit que sa façon de nommer un groupe musculaire soit
 * celle d'un jeu de données anglophone. Un sélecteur fermé l'obligerait à
 * choisir « lower back » pour désigner ses lombaires.
 *
 * ⚠️ Les entrées vides sont retirées : « biceps, , » ne doit pas produire deux
 * muscles anonymes qui pollueraient ensuite le filtre.
 */
export function parseList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

/** Le nom seul suffit à enregistrer : tout le reste est facultatif. */
export function canSaveExercise(draft: ExerciseDraft): boolean {
  return draft.name.trim() !== '' && draft.category.trim() !== '';
}

/**
 * ⚠️ **`null` explicite plutôt qu'omission** pour les champs vidés : le corps
 * de `PUT` remplace la ressource entière, donc omettre `equipment` reviendrait
 * au même — mais l'écrire rend l'intention visible, et le `PATCH`, lui, fait
 * bien la différence entre « ne touche pas » et « efface ».
 */
export function inputOfExerciseDraft(draft: ExerciseDraft): ExerciseInput {
  return {
    name: draft.name.trim(),
    category: draft.category.trim(),
    level: draft.level,
    force: draft.force === '' ? null : draft.force,
    mechanic: draft.mechanic === '' ? null : draft.mechanic,
    equipment: draft.equipment.trim() === '' ? null : draft.equipment.trim(),
    primaryMuscles: parseList(draft.primaryMuscles),
    secondaryMuscles: parseList(draft.secondaryMuscles),
    instructions: draft.instructions.trim() === '' ? null : draft.instructions.trim(),
  };
}

/**
 * Corps de `PATCH`.
 *
 * ⚠️ **Ne porte jamais `isFavorite`** : l'étoile de la liste le pose de son
 * côté, et l'inclure ici ferait qu'ouvrir puis enregistrer un exercice
 * réécrirait un favori qu'on n'a pas touché — sur la valeur qu'avait la fiche
 * au moment de l'ouverture, donc potentiellement périmée.
 */
export function updateOfExerciseDraft(draft: ExerciseDraft): UpdateExerciseBody {
  return inputOfExerciseDraft(draft);
}
